import {
  AppPermission,
  FieldTypes,
  FlespiMessage,
  FormStatusResult,
  RolePermissions,
  UnitOfMeasure,
  UserRole,
} from "@/utils/types";
import { type ClassValue, clsx } from "clsx";
import { minutesToHours } from "date-fns";
import { redirect } from "next/navigation";
import { twMerge } from "tailwind-merge";
import { FieldMapping } from "./profiles/schema";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isMessageValid(message: FlespiMessage) {
  return Object.keys(message).some(
    (x) =>
      x.startsWith("modbus") || // "Normal" modbus fields
      x.startsWith("ain") || // Fuel sensor analog
      x === "bs" || // VRM device message
      x === "soc", // Allye battery message
  );
}

export function encodedRedirect(type: "error" | "success", path: string, message: string) {
  return redirect(`${path}?${type}=${encodeURIComponent(message)}`);
}

export function getFormStatus(searchParams: {
  [key: string]: string | string[] | undefined;
}): FormStatusResult | null {
  const success = searchParams.success as string | undefined;
  if (success) return { success };
  const error = searchParams.error as string | undefined;
  if (error) return { error };
  const message = searchParams.message as string | undefined;
  if (message) return { message };

  return null;
}

export function getModbusValue({
  message,
  mapping,
  fieldMapping,
  selectMapping,
}: {
  message: FlespiMessage;
  mapping?: FieldMapping;
  fieldMapping?: FieldMapping[];
  selectMapping?: (x: FieldMapping) => boolean;
}) {
  mapping ||= fieldMapping?.find(selectMapping!);
  if (!mapping) {
    //throw new Error("Mapping not found: " + selectMapping?.toString());
    return -1;
  }

  const field = mapping.field as number;

  return (
    (message[`modbus_${field}`] ?? message[`modbus.sensor.value.${field}`]) *
    (mapping.multiplier || 1)
  );
}

export function getModbusValueSum({
  message,
  fieldMapping,
  term,
}: {
  message: FlespiMessage;
  fieldMapping: FieldMapping[];
  term: FieldTypes;
}) {
  return [1, 2, 3].reduce((acc, phase) => {
    const mapping = fieldMapping.find((x) => x.term === term && x.phase === phase);
    if (!mapping) return acc;
    const value = getModbusValue({ message, mapping });
    return acc + value;
  }, 0);
}

export function hasPermission(userRoles: UserRole[], ...permissions: AppPermission[]): boolean {
  return userRoles.some(
    (role) =>
      role === UserRole.GLOBAL_ADMIN || permissions.some((x) => RolePermissions[role]?.includes(x)),
  );
}

export function minutesToHoursExtended(minutes: number) {
  const hours = minutesToHours(minutes);
  const minutesLeft = minutes - hours * 60;
  const stringifiedHours = String(hours).length === 1 ? `0${hours}` : `${hours}`;
  const stringifiedMinutes =
    String(minutesLeft).length === 1 ? `0${minutesLeft}` : `${minutesLeft}`;
  return `${stringifiedHours}:${stringifiedMinutes}`;
}

export function generateEmptyMessage(deviceId: number): FlespiMessage {
  const message: FlespiMessage = {
    timestamp: new Date().getTime() / 1000,
    "device.id": deviceId,
  } as any;

  for (let i = 1; i <= 64; i++) {
    message[`modbus_${i}`] = 0;
  }

  return message;
}

export function formatTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

export function getDatesBetween(from: Date, until: Date): Date[] {
  const dates: Date[] = [];
  let current = new Date(from);

  while (current <= until) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

export function randomString(length: number = 24): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export const getVariant = (prc: number, unit: UnitOfMeasure) => {
  if (unit === "pF") {
    if (prc > 60) return "text-success";
    else if (prc > 40) return "text-warning";
    else return "text-destructive";
  } else {
    if (prc > 90) return "text-destructive";
    else if (prc > 70) return "text-warning";
    else return "text-success";
  }
};
